import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    list_blog_domains,
    create_blog_domain,
    update_blog_domain,
    delete_blog_domain,
    BlogDomain,
} from '@/api/blog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Plus, Edit2, Trash2, Globe2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { toast } from '@/hooks/use-toast';
import { handleServerError } from '@/utils/handle-server-error';

export function SuperAdminBlogDomains() {
    const { t } = useTranslation();
    const [domains, setDomains] = useState<BlogDomain[]>([]);
    const [loading, setLoading] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDomain, setEditingDomain] = useState<BlogDomain | null>(null);
    const [name, setName] = useState('');
    const [projectId, setProjectId] = useState('');
    const [saving, setSaving] = useState(false);

    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null; name: string }>({
        open: false,
        id: null,
        name: ''
    });

    const fetchDomains = async () => {
        try {
            setLoading(true);
            const data = await list_blog_domains();
            setDomains(data);
        } catch (error) {
            handleServerError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDomains();
    }, []);

    const openCreateDialog = () => {
        setEditingDomain(null);
        setName('');
        setProjectId('');
        setDialogOpen(true);
    };

    const openEditDialog = (domain: BlogDomain) => {
        setEditingDomain(domain);
        setName(domain.name);
        setProjectId(domain.project_id || '');
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!name.trim() || !projectId.trim()) return;
        setSaving(true);
        try {
            if (editingDomain) {
                await update_blog_domain(editingDomain.id, name.trim(), projectId.trim() || undefined);
                toast({ title: t('success'), description: t('domain_updated', 'Domain updated') });
            } else {
                await create_blog_domain(name.trim(), projectId.trim() || undefined);
                toast({ title: t('success'), description: t('domain_created', 'Domain created') });
            }
            setDialogOpen(false);
            fetchDomains();
        } catch (error) {
            handleServerError(error);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteDialog.id) return;
        setSaving(true);
        try {
            await delete_blog_domain(deleteDialog.id);
            toast({ title: t('success'), description: t('domain_deleted', 'Domain deleted') });
            setDeleteDialog({ open: false, id: null, name: '' });
            fetchDomains();
        } catch (error) {
            handleServerError(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-6">
            <PageHeader
                leading={
                    <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Globe2 className="h-5 w-5" />
                    </span>
                }
                title={t('blog_domains', 'Blog Domains')}
                description={t('manage_blog_domains_desc', 'Manage the domains available for blog posts, categories, and tags.')}
                actions={
                    <Button onClick={openCreateDialog}>
                        <Plus className="h-4 w-4 me-2" />
                        {t('add_domain', 'Add Domain')}
                    </Button>
                }
            />

            <Card>
                <CardHeader>
                    <CardTitle>{t('domain_list', 'Domain List')}</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                        </div>
                    ) : domains.length === 0 ? (
                        <div className="text-center p-8 text-gray-500">
                            {t('no_domains_found', 'No domains found. Create one to get started.')}
                        </div>
                    ) : (
                        <div className="rounded-md border">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 text-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 font-medium">{t('name')}</th>
                                        <th className="px-4 py-3 font-medium">{t('project_id', 'Project ID')}</th>
                                        <th className="px-4 py-3 font-medium">{t('created_at')}</th>
                                        <th className="px-4 py-3 font-medium text-right">{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {domains.map((domain) => (
                                        <tr key={domain.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 font-medium text-gray-900">{domain.name}</td>
                                            <td className="px-4 py-3 text-gray-500">{domain.project_id || '-'}</td>
                                            <td className="px-4 py-3 text-gray-500">{new Date(domain.created_at).toLocaleDateString()}</td>
                                            <td className="px-4 py-3 text-right">
                                                <Button variant="ghost" size="icon" onClick={() => openEditDialog(domain)}>
                                                    <Edit2 className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-red-500 hover:text-red-700"
                                                    onClick={() => setDeleteDialog({ open: true, id: domain.id, name: domain.name })}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={(open) => !open && setDialogOpen(false)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editingDomain ? t('edit_domain', 'Edit Domain') : t('create_domain', 'Create Domain')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('domain_dialog_desc', 'Enter the domain name and DigitalOcean project ID.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t('domain_name', 'Domain Name')} <span className="text-red-500">*</span></label>
                            <Input
                                placeholder="e.g. avocatpro.ma"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t('project_id', 'Project ID')} <span className="text-red-500">*</span></label>
                            <Input
                                placeholder="e.g. do-app-id-xyz"
                                value={projectId}
                                onChange={(e) => setProjectId(e.target.value)}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>{t('cancel')}</Button>
                        <Button onClick={handleSave} disabled={saving || !name.trim() || !projectId.trim()}>
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {t('save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, id: null, name: '' })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('delete_domain', 'Delete Domain')}</DialogTitle>
                        <DialogDescription>
                            {t('delete_domain_confirm', 'Are you sure you want to delete the domain {{name}}? This action cannot be undone.', { name: deleteDialog.name })}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialog({ open: false, id: null, name: '' })} disabled={saving}>
                            {t('cancel')}
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={saving}>
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {t('delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
